// src/vendor/log-client.ts
var LogClient = class {
  buffer = [];
  flushTimer = null;
  flushChain = Promise.resolve();
  sessionId;
  userId = null;
  service;
  platform;
  flushEndpoint;
  productKey;
  maxBatchSize;
  maxBufferSize;
  fetchFn;
  consoleOnLog;
  context;
  maxLogsPerSignaturePerWindow;
  signatureWindowMs;
  // Per-signature sliding-window state for the rate cap. Keyed by
  // normalized `level:message` signature. Pruned in doFlush() so it can't
  // grow without bound across a long-lived session.
  sigWindows = /* @__PURE__ */ new Map();
  constructor(options) {
    this.service = options.service;
    this.platform = options.platform;
    this.flushEndpoint = options.flushEndpoint;
    this.productKey = options.productKey ?? null;
    this.maxBatchSize = options.maxBatchSize ?? 50;
    this.maxBufferSize = options.maxBufferSize ?? 500;
    this.fetchFn = options.fetchFn ?? fetch.bind(globalThis);
    this.consoleOnLog = options.consoleOnLog ?? false;
    this.context = options.context;
    this.maxLogsPerSignaturePerWindow = options.maxLogsPerSignaturePerWindow ?? 50;
    this.signatureWindowMs = options.signatureWindowMs ?? 6e4;
    this.sessionId = crypto.randomUUID();
    const intervalMs = options.flushIntervalMs ?? 5e3;
    this.flushTimer = setInterval(() => {
      this.flush().catch(() => {
      });
    }, intervalMs);
  }
  setUserId(userId) {
    this.userId = userId;
  }
  /** Merge fields into the static ambient context applied to every entry. */
  updateContext(partial) {
    this.context = { ...this.context, ...partial };
  }
  getSessionId() {
    return this.sessionId;
  }
  startCorrelation() {
    return crypto.randomUUID();
  }
  trace(msg, opts) {
    this.log("trace", msg, opts);
  }
  debug(msg, opts) {
    this.log("debug", msg, opts);
  }
  info(msg, opts) {
    this.log("info", msg, opts);
  }
  warn(msg, opts) {
    this.log("warn", msg, opts);
  }
  error(msg, opts) {
    this.log("error", msg, opts);
  }
  fatal(msg, opts) {
    this.log("fatal", msg, opts);
  }
  navigation(screen, opts) {
    this.log("info", `Navigated to ${screen}`, { ...opts, eventType: "navigation", data: { ...opts?.data, screen } });
  }
  action(msg, opts) {
    this.log("info", msg, { ...opts, eventType: "action" });
  }
  log(level, msg, opts) {
    const entry = {
      timestamp: Date.now(),
      level,
      service: this.service,
      platform: this.platform,
      msg,
      session_id: this.sessionId,
      user_id: this.userId ?? void 0,
      event_type: opts?.eventType,
      correlation_id: opts?.correlationId,
      context: this.context,
      data: opts?.data,
      error_stack: opts?.error?.stack,
      error_message: opts?.error?.message
    };
    if (this.consoleOnLog) {
      const method = level === "trace" || level === "debug" ? "debug" : level === "info" ? "info" : level === "warn" ? "warn" : "error";
      if (opts?.error !== void 0 && opts?.data !== void 0) {
        console[method](msg, opts.error, opts.data);
      } else if (opts?.error !== void 0) {
        console[method](msg, opts.error);
      } else if (opts?.data !== void 0) {
        console[method](msg, opts.data);
      } else {
        console[method](msg);
      }
    }
    if (this.throttleEnabled) {
      const key = this.signatureKey(level, msg);
      const now = entry.timestamp;
      let w = this.sigWindows.get(key);
      if (!w || now - w.windowStart >= this.signatureWindowMs) {
        if (w && w.suppressed > 0) {
          this.enqueue(this.buildSuppressionSummary(key, w.suppressed), true);
        }
        w = { windowStart: now, count: 0, suppressed: 0 };
        this.sigWindows.set(key, w);
      }
      w.count++;
      if (w.count > this.maxLogsPerSignaturePerWindow) {
        w.suppressed++;
        return;
      }
    }
    this.enqueue(entry, true);
  }
  /** True when a finite, positive per-signature cap is configured. */
  get throttleEnabled() {
    return Number.isFinite(this.maxLogsPerSignaturePerWindow) && this.maxLogsPerSignaturePerWindow > 0;
  }
  /**
   * Normalized signature for the rate cap. Collapses varying ids (UUIDs, long
   * digit runs) so "Order 123 not found" and "Order 456 not found" share a
   * signature — a loop that only varies an id is still caught.
   */
  signatureKey(level, msg) {
    const normalized = msg.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "<uuid>").replace(/\d{2,}/g, "<n>").slice(0, 200);
    return `${level}:${normalized}`;
  }
  /** Synthesize the single "suppressed N" summary entry for a rolled window. */
  buildSuppressionSummary(key, suppressed) {
    const idx = key.indexOf(":");
    const level = idx >= 0 ? key.slice(0, idx) : "warn";
    const sigMsg = idx >= 0 ? key.slice(idx + 1) : key;
    const windowSec = Math.round(this.signatureWindowMs / 1e3);
    return {
      timestamp: Date.now(),
      level,
      service: this.service,
      platform: this.platform,
      msg: `Suppressed ${suppressed} duplicate log(s) in ${windowSec}s: ${sigMsg}`,
      session_id: this.sessionId,
      user_id: this.userId ?? void 0,
      event_type: "system",
      context: this.context,
      data: { domain: "log_throttle", log_suppressed_count: suppressed, signature: sigMsg }
    };
  }
  /**
   * Emit pending suppression summaries for any expired window and prune idle
   * signatures so the map can't grow unbounded. Called at the top of every
   * flush — covers the case where a runaway loop stops and never re-emits the
   * signature that would otherwise trigger the summary in log().
   */
  sweepSignatureWindows() {
    if (!this.throttleEnabled || this.sigWindows.size === 0) return;
    const now = Date.now();
    for (const [key, w] of this.sigWindows) {
      if (now - w.windowStart < this.signatureWindowMs) continue;
      if (w.suppressed > 0) {
        this.enqueue(this.buildSuppressionSummary(key, w.suppressed), false);
      }
      this.sigWindows.delete(key);
    }
  }
  /** Append an entry, honoring the buffer cap and the auto-flush threshold. */
  enqueue(entry, allowAutoFlush) {
    if (this.buffer.length >= this.maxBufferSize) {
      this.buffer.shift();
    }
    this.buffer.push(entry);
    if (allowAutoFlush && this.buffer.length >= this.maxBatchSize) {
      this.flush().catch(() => {
      });
    }
  }
  flush() {
    this.flushChain = this.flushChain.then(() => this.doFlush());
    return this.flushChain;
  }
  async doFlush() {
    this.sweepSignatureWindows();
    if (this.buffer.length === 0) return;
    const batch = this.buffer.splice(0, this.maxBatchSize);
    try {
      const headers = { "Content-Type": "application/json" };
      if (this.productKey) headers["X-Product-Key"] = this.productKey;
      await this.fetchFn(this.flushEndpoint, {
        method: "POST",
        headers,
        body: JSON.stringify({ entries: batch })
      });
    } catch {
      const space = this.maxBufferSize - this.buffer.length;
      if (space > 0) {
        this.buffer.unshift(...batch.slice(0, space));
      }
    }
  }
  destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }
};
function createLogClient(options) {
  return new LogClient(options);
}

// src/logger.ts
var RestaurantLogger = class _RestaurantLogger {
  static instance = null;
  client;
  constructor(client) {
    this.client = client;
  }
  static create(options) {
    const client = createLogClient(options);
    _RestaurantLogger.instance = new _RestaurantLogger(client);
    return _RestaurantLogger.instance;
  }
  static init(client) {
    _RestaurantLogger.instance = new _RestaurantLogger(client);
    return _RestaurantLogger.instance;
  }
  /** Reset singleton — for test teardown only */
  static reset() {
    _RestaurantLogger.instance = null;
  }
  static getInstance() {
    if (!_RestaurantLogger.instance) {
      throw new Error("RestaurantLogger not initialized. Call RestaurantLogger.init(client) first.");
    }
    return _RestaurantLogger.instance;
  }
  // Passthrough (all levels)
  trace(msg, data) {
    this.client.trace(msg, { data });
  }
  debug(msg, data) {
    this.client.debug(msg, { data });
  }
  info(msg, data) {
    this.client.info(msg, { data });
  }
  warn(msg, data) {
    this.client.warn(msg, { data });
  }
  error(msg, data) {
    this.client.error(msg, { data });
  }
  fatal(msg, data) {
    this.client.fatal(msg, { data });
  }
  flush() {
    void this.client.flush();
  }
  /**
   * Tear down the underlying LogClient (clears flush timer, buffered
   * entries are discarded). Intended for React Native fast-refresh
   * teardown where a new singleton replaces the old one. After calling
   * destroy() the caller should also call RestaurantLogger.reset() to
   * null out the singleton slot.
   *
   * `destroy()` is instance-scoped (because `client` is private).
   * `reset()` is static (singleton slot). Do not confuse them.
   */
  destroy() {
    this.client.destroy();
  }
  setUserId(userId) {
    this.client.setUserId(userId);
  }
  /**
   * Merge fields into the static ambient context attached to EVERY log
   * entry (the `context` column). Use for attribution that resolves after
   * logger creation — e.g. a persisted install_id read asynchronously.
   */
  updateContext(context) {
    this.client.updateContext(context);
  }
  getSessionId() {
    return this.client.getSessionId();
  }
  startCorrelation() {
    return this.client.startCorrelation();
  }
  // Domain helpers
  navigation(screen, data) {
    this.client.info(`navigation:${screen}`, {
      eventType: "navigation",
      data: { domain: "navigation", screen, ...data }
    });
  }
  auth(action, data) {
    this.client.info(`auth:${action}`, {
      eventType: "action",
      data: { domain: "auth", action, ...data }
    });
  }
  order(action, data) {
    this.client.info(`order:${action}`, {
      eventType: "action",
      data: { domain: "order", action, ...data }
    });
  }
  cart(action, data) {
    this.client.info(`cart:${action}`, {
      eventType: "action",
      data: { domain: "cart", action, ...data }
    });
  }
  checkout(action, data) {
    this.client.info(`checkout:${action}`, {
      eventType: "action",
      data: { domain: "checkout", action, ...data }
    });
  }
  menu(action, data) {
    this.client.info(`menu:${action}`, {
      eventType: "action",
      data: { domain: "menu", action, ...data }
    });
  }
  reward(action, data) {
    this.client.info(`reward:${action}`, {
      eventType: "action",
      data: { domain: "reward", action, ...data }
    });
  }
  delivery(action, data) {
    this.client.info(`delivery:${action}`, {
      eventType: "action",
      data: { domain: "delivery", action, ...data }
    });
  }
  offers(action, data) {
    this.client.info(`offers:${action}`, {
      eventType: "action",
      data: { domain: "offers", action, ...data }
    });
  }
  favorites(action, data) {
    this.client.info(`favorites:${action}`, {
      eventType: "action",
      data: { domain: "favorites", action, ...data }
    });
  }
  notifications(action, data) {
    this.client.info(`notifications:${action}`, {
      eventType: "action",
      data: { domain: "notifications", action, ...data }
    });
  }
  config(action, data) {
    this.client.info(`config:${action}`, {
      eventType: "action",
      data: { domain: "config", action, ...data }
    });
  }
  sync(action, data) {
    this.client.info(`sync:${action}`, {
      eventType: "action",
      data: { domain: "sync", action, ...data }
    });
  }
  // Error
  applicationError(error, context) {
    this.client.error(`Application error: ${error.message}`, {
      eventType: "error",
      error,
      data: { domain: "error", error_name: error?.name, ...context }
    });
  }
};
function safeGetLogger() {
  try {
    return RestaurantLogger.getInstance();
  } catch {
    return null;
  }
}

// src/error-context.ts
function isObject(v) {
  return v !== null && typeof v === "object";
}
function extractErrorContext(err) {
  const out = {};
  if (!isObject(err)) return out;
  const source = isObject(err.originalError) ? err.originalError : err;
  const config = isObject(source.config) ? source.config : void 0;
  if (config && typeof config.url === "string") {
    out.request_url = config.url;
    if (typeof config.method === "string") {
      out.request_method = config.method;
    }
  }
  const response = isObject(source.response) ? source.response : void 0;
  const status = typeof response?.status === "number" ? response.status : typeof source.status === "number" ? source.status : void 0;
  if (status !== void 0 && out.request_url !== void 0) {
    out.response_status = status;
  }
  if (typeof err.type === "string") {
    out.error_type = err.type;
  }
  return out;
}

// src/error-fields.ts
function isObject2(v) {
  return v !== null && typeof v === "object";
}
function safeStringify(v) {
  try {
    return JSON.stringify(v) ?? String(v);
  } catch {
    return String(v);
  }
}
function copyKnownFields(err, out) {
  if (typeof err.code === "string" && err.code !== "") out.error_code = err.code;
  if (typeof err.details === "string" && err.details !== "") out.error_details = err.details;
  if (typeof err.hint === "string" && err.hint !== "") out.error_hint = err.hint;
  if (typeof err.status === "number") out.error_status = err.status;
}
function errorLogFields(err) {
  if (isObject2(err) && typeof err.message === "string" && err.message !== "") {
    const out = { error: err.message };
    copyKnownFields(err, out);
    return out;
  }
  if (isObject2(err)) {
    const out = { error: safeStringify(err) };
    if (typeof err.name === "string" && err.name !== "" && err.name !== "Error") out.error_name = err.name;
    copyKnownFields(err, out);
    return out;
  }
  return { error: String(err) };
}
var TRANSIENT_STATUSES = /* @__PURE__ */ new Set([0, 408, 425, 429, 500, 502, 503, 504]);
var TRANSIENT_MESSAGE = /^(FetchError|TypeError|AbortError)\b|Network request failed|aborted|timed? ?out/i;
function isLikelyTransientError(err) {
  if (!isObject2(err)) return false;
  if (typeof err.status === "number" && TRANSIENT_STATUSES.has(err.status)) return true;
  if (err.name === "AbortError" || err.code === "ABORT_ERR") return true;
  if (typeof err.message === "string") {
    if (err.message === "") return true;
    if (TRANSIENT_MESSAGE.test(err.message)) return true;
  }
  return false;
}
export {
  RestaurantLogger,
  errorLogFields,
  extractErrorContext,
  isLikelyTransientError,
  safeGetLogger
};
//# sourceMappingURL=index.js.map